import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiDelete, apiGet, apiPatch, apiPost } from '@/lib/api';

export type ChatUser = {
  id: string;
  name: string;
  designation?: string | null;
  avatarFileId?: string | null;
};

export type ChatMember = {
  userId: string;
  isAdmin: boolean;
  /** When their client last picked messages up. The second tick. */
  lastDeliveredAt: string | null;
  /** When they last looked at the channel. The blue tick. */
  lastReadAt: string | null;
  user: ChatUser;
};

export type ChatChannel = {
  id: string;
  kind: 'PUBLIC' | 'PRIVATE' | 'DIRECT' | 'GROUP' | 'ROLE';
  name: string | null;
  title: string;
  topic: string | null;
  isDefault: boolean;
  /** For a ROLE channel: the roles it is open to. */
  roleIds: string[];
  entityType: string | null;
  entityId: string | null;
  lastMessageAt: string | null;
  archivedAt: string | null;
  createdAt: string;
  members: ChatMember[];
  unread?: number;
};

export type ChatMessage = {
  id: string;
  body: string;
  isSystem: boolean;
  fileIds: string[];
  mentions: string[];
  replyToId: string | null;
  editedAt: string | null;
  createdAt: string;
  author: ChatUser | null;
  replyTo?: { id: string; body: string; author: { id: string; name: string } | null } | null;
  reactions: { emoji: string; userId: string }[];
};

type ChannelsResponse = {
  channels: ChatChannel[];
  discoverable: {
    id: string;
    name: string | null;
    topic: string | null;
    kind: string;
    roleIds: string[];
  }[];
};

type MessagesResponse = {
  messages: ChatMessage[];
  cursor: string | null;
  hasMore: boolean;
};

/**
 * How often an open conversation asks for new messages.
 *
 * Three seconds reads as live in a chat window and costs one indexed seek that
 * returns nothing almost every time. The channel list polls a third as often —
 * an unread badge arriving a few seconds late costs nobody anything.
 */
const MESSAGE_POLL_MS = 3_000;
const LIST_POLL_MS = 10_000;

export function useChatChannels() {
  return useQuery({
    queryKey: ['chat', 'channels'],
    queryFn: () => apiGet<ChannelsResponse>('/chat/channels'),
    refetchInterval: LIST_POLL_MS,
  });
}

/** The unread badge. Pass `false` for a role without chat, or it would poll a 403. */
export function useChatUnread(enabled = true) {
  return useQuery({
    queryKey: ['chat', 'unread'],
    queryFn: () => apiGet<{ total: number; channels: Record<string, number> }>('/chat/unread'),
    refetchInterval: LIST_POLL_MS,
    enabled,
  });
}

export function useChatChannel(id: string | undefined) {
  return useQuery({
    queryKey: ['chat', 'channel', id],
    queryFn: () => apiGet<ChatChannel>(`/chat/channels/${id}`),
    enabled: Boolean(id),
  });
}

/** How many recent messages each poll re-reads, not just the new ones. */
const REFRESH_WINDOW = 20;

/**
 * The message list for the open channel.
 *
 * Each poll asks for everything since a point a little way back rather than
 * strictly after the newest message it holds. That window is what makes a
 * colleague's edit, reaction or deletion show up at all: a cursor pinned to
 * the last message can only ever return *new* messages, so a reaction added
 * to something said a minute ago would stay invisible until a page reload.
 *
 * Older pages are prepended by `loadOlder`, so scrolling back does not
 * re-fetch what is already on screen.
 */
export function useChatMessages(channelId: string | undefined) {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['chat', 'messages', channelId],
    queryFn: async ({ queryKey }) => {
      const existing = queryClient.getQueryData<MessagesResponse>(queryKey);
      // A hair before the window's first message, because `after` is exclusive
      // and we want that message included in the refresh.
      const anchor = existing?.messages.at(-REFRESH_WINDOW)?.createdAt;
      const cursor = anchor ? new Date(new Date(anchor).getTime() - 1).toISOString() : undefined;

      const page = await apiGet<MessagesResponse>(`/chat/channels/${channelId}/messages`, {
        params: cursor ? { after: cursor } : { limit: 50 },
      });

      if (!existing) return page;

      // The page is authoritative from the window's start onwards: keep only
      // what is older than it, then take the server's version of the rest. An
      // edit therefore lands in place, and a message deleted elsewhere simply
      // is not in the page and so disappears.
      const windowStart = cursor ?? page.messages[0]?.createdAt;
      const older = windowStart
        ? existing.messages.filter((message) => message.createdAt < windowStart)
        : existing.messages;

      return { ...page, messages: [...older, ...page.messages], hasMore: existing.hasMore };
    },
    enabled: Boolean(channelId),
    refetchInterval: MESSAGE_POLL_MS,
    // A window left in the background for an hour should not replay an hour of
    // polls the moment it comes forward.
    refetchIntervalInBackground: false,
  });

  const loadOlder = async () => {
    if (!channelId) return;
    const existing = queryClient.getQueryData<MessagesResponse>(['chat', 'messages', channelId]);
    const oldest = existing?.messages[0]?.createdAt;
    if (!oldest) return;

    const page = await apiGet<MessagesResponse>(`/chat/channels/${channelId}/messages`, {
      params: { before: oldest, limit: 50 },
    });

    queryClient.setQueryData<MessagesResponse>(['chat', 'messages', channelId], (current) =>
      current
        ? { ...current, messages: [...page.messages, ...current.messages], hasMore: page.hasMore }
        : page,
    );
  };

  return { ...query, loadOlder };
}

export function useSendMessage(channelId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { body: string; mentions?: string[]; replyToId?: string }) =>
      apiPost<ChatMessage>(`/chat/channels/${channelId}/messages`, input),
    onSuccess: (message) => {
      // Appended straight away rather than waiting for the next poll, so the
      // message you just typed appears the instant it is accepted.
      queryClient.setQueryData<MessagesResponse>(['chat', 'messages', channelId], (current) =>
        current && !current.messages.some((existing) => existing.id === message.id)
          ? { ...current, messages: [...current.messages, message] }
          : current,
      );
      void queryClient.invalidateQueries({ queryKey: ['chat', 'channels'] });
    },
  });
}

/**
 * Editing, deleting and reacting all apply before the server answers.
 *
 * A chat window that waits for a round trip feels broken, not slow — you tap a
 * reaction and nothing moves, so you tap it again. Each of these three writes
 * the change into the cache in `onMutate`, keeps the previous list, and puts
 * it back if the request fails.
 */
function useOptimisticMessageEdit<TInput, TResult>(
  channelId: string | undefined,
  mutationFn: (input: TInput) => Promise<TResult>,
  apply: (messages: ChatMessage[], input: TInput) => ChatMessage[],
) {
  const queryClient = useQueryClient();
  const key = ['chat', 'messages', channelId];

  return useMutation({
    mutationFn,
    onMutate: async (input: TInput) => {
      // Stop an in-flight poll from landing on top of the optimistic edit.
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<MessagesResponse>(key);

      queryClient.setQueryData<MessagesResponse>(key, (current) =>
        current ? { ...current, messages: apply(current.messages, input) } : current,
      );

      return { previous };
    },
    onError: (_error, _input, context) => {
      if (context?.previous) queryClient.setQueryData(key, context.previous);
    },
  });
}

export function useEditMessage(channelId: string | undefined) {
  return useOptimisticMessageEdit(
    channelId,
    ({ id, body }: { id: string; body: string }) =>
      apiPatch<{ id: string; body: string; editedAt: string }>(`/chat/messages/${id}`, { body }),
    (messages, { id, body }) =>
      messages.map((message) =>
        message.id === id
          ? { ...message, body: body.trim(), editedAt: new Date().toISOString() }
          : message,
      ),
  );
}

export function useDeleteMessage(channelId: string | undefined) {
  return useOptimisticMessageEdit(
    channelId,
    async (id: string) => {
      await apiDelete(`/chat/messages/${id}`);
      return { id };
    },
    (messages, id) => messages.filter((message) => message.id !== id),
  );
}

export function useReact(channelId: string | undefined, meId: string) {
  return useOptimisticMessageEdit(
    channelId,
    ({ id, emoji }: { id: string; emoji: string }) =>
      apiPost<{ messageId: string; emoji: string; on: boolean }>(`/chat/messages/${id}/react`, {
        emoji,
      }),
    (messages, { id, emoji }) =>
      messages.map((message) => {
        if (message.id !== id) return message;
        const reactions = message.reactions ?? [];
        const mine = reactions.some(
          (reaction) => reaction.emoji === emoji && reaction.userId === meId,
        );
        // Tapping the same reaction twice takes it off, here and on the server.
        return {
          ...message,
          reactions: mine
            ? reactions.filter(
                (reaction) => !(reaction.emoji === emoji && reaction.userId === meId),
              )
            : [...reactions, { emoji, userId: meId }],
        };
      }),
  );
}

export function useMarkRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (channelId: string) => apiPost<{ channelId: string }>(`/chat/channels/${channelId}/read`, {}),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['chat', 'unread'] });
      void queryClient.invalidateQueries({ queryKey: ['chat', 'channels'] });
    },
  });
}

export function useSaveChannel() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      id?: string;
      name?: string | null;
      topic?: string | null;
      kind?: 'PUBLIC' | 'PRIVATE' | 'ROLE';
      memberIds?: string[];
      roleIds?: string[];
    }) => apiPost<ChatChannel>('/chat/channels', input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['chat'] }),
  });
}

export function useOpenDirect() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) => apiPost<ChatChannel>('/chat/direct', { userId }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['chat', 'channels'] }),
  });
}

export function useOpenGroup() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (userIds: string[]) => apiPost<ChatChannel>('/chat/group', { userIds }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['chat', 'channels'] }),
  });
}

export function useJoinChannel() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiPost<ChatChannel>(`/chat/channels/${id}/join`, {}),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['chat'] }),
  });
}

export function useLeaveChannel() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiPost<{ id: string }>(`/chat/channels/${id}/leave`, {}),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['chat'] }),
  });
}

export function useUpdateChannelMembers(channelId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { add?: string[]; remove?: string[] }) =>
      apiPatch<ChatChannel>(`/chat/channels/${channelId}/members`, input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['chat'] }),
  });
}

export function useChatSearch(q: string) {
  return useQuery({
    queryKey: ['chat', 'search', q],
    queryFn: () =>
      apiGet<
        {
          id: string;
          body: string;
          createdAt: string;
          author: { id: string; name: string } | null;
          channel: { id: string; name: string | null; kind: string };
        }[]
      >('/chat/search', { params: { q } }),
    enabled: q.trim().length >= 2,
  });
}

/** The roles a channel can be opened to, and how many people hold each. */
export type ChatRoleOption = { id: string; name: string; key: string; userCount: number };

export function useChatRoles(enabled = true) {
  return useQuery({
    queryKey: ['chat', 'roles'],
    queryFn: () => apiGet<ChatRoleOption[]>('/chat/roles'),
    staleTime: 5 * 60 * 1000,
    enabled,
  });
}
